import React, { useState, useEffect, createContext } from "react";
import { useDispatch, useSelector } from 'react-redux';
import {CommonDialogBox} from "components";
import { Box, Divider } from "@mui/material";
import CustomStepper from "components/CustomStepper";
import FormDetails from "./FormDetails";
import FormCoordinates from "./FormCoordinates";
import FormProjection from "./FormProjection";
import FormDimensions from "./FormDimensions";
import ButtonStepper from "components/ButtonStepper";
import { Scrollbars } from "react-custom-scrollbars";
import './style.css';
import {
	closeNewDialog,
	closeEditDialog,
  remoteCreateProject,
  remoteUpdateProject,
  resetProject,
  resetLastRequest,
  removeRequestDone
} from "store/projects";
import _ from "utils/@lodash";
import { useDeepCompareEffect } from 'hooks';
import NotificationModel from 'components/notificationPanel/model/NotificationModel';
import { addNotification } from 'components/notificationPanel/store/dataSlice';
import { updateData } from "services/CacheService";
import EventsEmitter from "events/EventsEmitter";
import { trackEvent } from "components/Tracking";
const StepperForm: any[] = [
  FormDetails,
  FormCoordinates,
  FormProjection,
  FormDimensions
]
export const FormContext = createContext({});

const steps = ['Project Details', 'Coordinates', 'Geographic Projection', 'Dimensions'];

const _renderStepContent = (step: number) => {
  const Form = StepperForm[step - 1] ? StepperForm[step - 1] : <div>Not Found</div>
  return <Form />;
}

const formFields = [[
  'name',
  'description',
], [
  'timezone',
  'hdatum',
  'vdatum',
], [
  'projection',
  'utmZone',
  'utmNorthing',
  'falseEasting',
  'falseNorthing',
  'latitudeOrigin',
  'longitudeMeridian',
  'scaleFactor',
  'firstStdParallel',
  'secondStdParallel',
  'amgZone',
  'mgaZone',
  'loZone',
], [
  'voxelSize',
  'units',
  'originX',
  'originY',
  'originZ',
  'minX',
  'minY',
  'minZ',
  'maxX',
  'maxY',
  'maxZ',
]]

const projectHelpLinks = [
  'project-details',
  'project-coordinate-system',
  'project-geographic-projection',
  'project-dimensions'
]

const CreateProjectPanel: React.FC = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isLastStep, setIsLastStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState(false);
	const dispatch = useDispatch<any>();
	const projects = useSelector((state: any) => state.projects);
  const [formData, setFormData] = useState<any>({});
  const [formPayloadData, setFormPayloadData] = useState<any>({});
  const [defaultValues, setDefaultValues] = useState<any>({
    name: 'New Project',
    description: '',
  });
  const [currentFields, setCurrentFields] = useState<any>([
    'name',
    'description'
  ]);
  const {
    id:projectId,
		requestDone,
		success,
		error,
		lastRequest,
    lastCreated
  } = projects
  

  useEffect(() => {
    if(!projects?.dialogBox?.props?.open) return;
    const data = {...(projects?.dialogBox?.data || {})};
    setFormData(JSON.parse(JSON.stringify(data)));
  }, [projects?.dialogBox]);

  useEffect(() => {
    if (_.isEmpty(formData)) return;

    const data = formFields[activeStep - 1].reduce((data: any, field: string) => ({
        ...data,
        [field]: field in formData ? formData[field] : ''
    }), {});
    if (activeStep === 4) {
      data.voxelSize = parseFloat(data.voxelSize);
      data.originX = parseInt(data.originX);
      data.originY = parseInt(data.originY);
      data.originZ = parseInt(data.originZ);
    }
    setDefaultValues(data);
    setCurrentFields(formFields[activeStep - 1]);
  }, [activeStep, formFields, formData]);

  useEffect(() => {
    setIsLastStep(activeStep === steps.length);
  }, [activeStep, steps]);

  const resetForm = () => {
    dispatch(resetProject());
    setActiveStep(1);
  }

  const closeComposeDialog = () => {
    resetForm();
    trackEvent("Catalog", "Create Project", "Close")
		return projects.dialogBox.type === 'edit' ? dispatch(closeEditDialog()) : dispatch(closeNewDialog());
	}

  const saveData = (data: any) => {
    const _data = JSON.parse(JSON.stringify({
      ...formData,
      ...data
    }))
    setFormData(_data)

  }

const createProject = () => {
  var projection = formData.projection;

  const data: any = {
      name: formData.name,
      file_date: Date.now(),
      description: formData.description,
      timezone: formData.timezone,
      coord_hdatum: formData.hdatum,
      coord_vdatum: formData.vdatum,
      coord_projection: formData.projection,
      voxel_size: formData.voxelSize,
      coord_origin_x: formData.originX,
      coord_origin_y: formData.originY,
      coord_origin_z: formData.originZ,
      coord_unit: formData.units,
      type: "PROJECT",
  };

  if (projection === "utm") {
      data["coord_projection_utm_northing"] = formData.utmNorthing;
      data["coord_projection_utm_easting"] = formData.utmZone;
  } else if (projection === "tm") {
      data["coord_projection_tm_falseEasting"] = formData.falseEasting;
      data["coord_projection_tm_falseNorthing"] = formData.falseNorthing;
      data["coord_projection_tm_latOriginDeg"] = formData.latitudeOrigin;
      data["coord_projection_tm_longMeridianDeg"] = formData.longitudeMeridian;
      data["coord_projection_tm_scaleFactor"] = formData.scaleFactor;
  } else if (projection === "lcc") {
      data["coord_projection_lcc_falseEasting"] = formData.falseEasting;
      data["coord_projection_lcc_falseNorthing"] = formData.falseNorthing;
      data["coord_projection_lcc_latOfOriginDeg"] = formData.latitudeOrigin;
      data["coord_projection_lcc_longOfOriginDeg"] = formData.longitudeMeridian;
      data["coord_projection_lcc_firstStdParallelDeg"] = formData.firstStdParallel;
      data["coord_projection_lcc_secondStdParallelDeg"] = formData.secondStdParallel;
  } else if (projection === "aeac") {
      data["coord_projection_aeac_falseEasting"] = formData.falseEasting;
      data["coord_projection_aeac_falseNorthing"] = formData.falseNorthing;
      data["coord_projection_aeac_latOfOriginDeg"] = formData.latitudeOrigin;
      data["coord_projection_aeac_longOfOriginDeg"] = formData.longitudeMeridian;
      data["coord_projection_aeac_firstStdParallelDeg"] = formData.firstStdParallel;
      data["coord_projection_aeac_secondStdParallelDeg"] = formData.secondStdParallel;
  } else if (projection === "amg") {
      data["coord_projection_amg_zone"] = formData.amgZone;
  } else if (projection === "mga") {
      data["coord_projection_mga_zone"] = formData.mgaZone;
  } else if (projection === "lo") {
      data["coord_projection_lo_zone"] = formData.loZone;
  }
  data["access_level"] = "full";
  const dispachAction = projects.dialogBox.type === 'edit' ? remoteUpdateProject : remoteCreateProject;
  dispatch(resetLastRequest());
	dispatch(dispachAction(data));
  setFormPayloadData(data);
  setSubmitForm(false);
}

  useEffect(() => {
    if (!submitForm)  return ;
    createProject();
  }, [submitForm]);

  const onSubmit = () => {
    setSubmitForm(true);
    trackEvent("Catalog", "Create Project", "Submit")
  };

  const _handleSubmit = () => {
    if (isLastStep) {
      onSubmit();
    } else {
      console.log("Step : " + activeStep)
      trackEvent("Catalog", "Create Project", steps[activeStep])
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }

  const _handleBack = () => {
    if (activeStep === 1) {
      return;
    }
    setActiveStep(activeStep - 1);
  }

	useDeepCompareEffect(() => {
		if (lastRequest !== 'create' && lastRequest !== 'update'/* && lastRequest !== 'delete'*/) {
			return;
		}
		if (requestDone && success) {
		  dispatch(addNotification(NotificationModel({ message: 'The system is successfully updated.', options: { variant: 'success' } })));
			dispatch(removeRequestDone());
      closeComposeDialog();
      if (lastRequest === 'create') {
        updateData("create", "projects", lastCreated.id, formPayloadData, (id: string) => {});
        //setTimeout(() => {
          //window.location.reload();
          EventsEmitter.emit("reloadData", "");
        //}, 100);
        
      }
      if (lastRequest === 'update') {
        updateData("update", "projects", projectId, {
          ...formPayloadData,
          ID: projectId
        }, (id: string) => {});

        //setTimeout(() => {
          //window.location.reload();
          EventsEmitter.emit("reloadData", "");
        //}, 100);
        
      }
		}
		if (requestDone && error) {
			dispatch(addNotification(NotificationModel({ message: 'An error occurred updating the system.', options: { variant: 'error' } })));
			dispatch(removeRequestDone());
		}
		
	}, [dispatch, lastRequest, addNotification, success, error, requestDone, NotificationModel]);

  const DialogAction = () => {
    return (
      <ButtonStepper
        steps={steps}
        activeStep={activeStep}
        onClickBack={_handleBack}
        loading={isSubmitting}
        formId="projectForm"
      />
    )
}

  return (
    <FormContext.Provider value = {{ defaultValues, isLastStep, setIsLastStep, saveData, setIsSubmitting, _handleSubmit, currentFields }}>
      <CommonDialogBox
        classes={{
          paper: 'm-24 rounded-8 white-bg'
        }}
        {...projects.dialogBox.props}
        handleClose={closeComposeDialog}
        helpLinkCode={projectHelpLinks[activeStep - 1]}
        fullWidth
        maxWidth="sm"
        subtitle1={projects.dialogBox.type === 'new' ? 'New Project' : 'Project Edit'}
        titleh5={ projects.dialogBox.type === 'edit' ? formData.name : '' }
        dialogAction={<DialogAction />}
        displayScrollBar={false}
      >
        <div>
          <Box pt={2}>
            <CustomStepper activeStep={activeStep} steps={steps} />
          </Box>
          <Divider sx={{ mt: 3,mb: 3,mr:2,ml:2 }} />
          <Scrollbars
                autoHeight
                autoHeightMax={300}
                style={{ overflowX: "hidden", width: "100%," }}
          >
            <Box p={2}>
              {_renderStepContent(activeStep)}
            </Box>
          </Scrollbars>
        </div>
      </CommonDialogBox>
    </FormContext.Provider>
  );
}

export default CreateProjectPanel;
